import { useCallback, useSyncExternalStore } from 'react';

// A drop-in, SSR-safe alternative to @uidotdev/usehooks's useLocalStorage.
// That library's getServerSnapshot unconditionally throws ("useLocalStorage is
// a client-only hook") instead of returning a fallback, which crashes any
// component that reaches it during server rendering. This hook returns
// `defaultValue` on the server/first paint instead, then syncs to the real
// stored value (and to other tabs/frames) via the native `storage` event —
// same event both this hook's own setter and teamsStore's broadcastAuthState
// dispatch on write.
function subscribe(key: string, callback: () => void) {
  const listener = (e: StorageEvent) => {
    if (e.key === key) callback();
  };
  window.addEventListener('storage', listener);
  return () => window.removeEventListener('storage', listener);
}

export function useSyncedLocalStorage<T>(key: string, defaultValue: T): [T, (value: T) => void] {
  const getSnapshot = () => {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  };
  const getServerSnapshot = () => null;

  const raw = useSyncExternalStore(
    (callback) => subscribe(key, callback),
    getSnapshot,
    getServerSnapshot
  );

  let value = defaultValue;
  if (raw !== null) {
    try {
      value = JSON.parse(raw) as T;
    } catch {
      value = defaultValue;
    }
  }

  const setValue = useCallback(
    (next: T) => {
      const newValue = JSON.stringify(next);
      window.localStorage.setItem(key, newValue);
      // storage events don't fire in the writing window itself — dispatch
      // manually so same-window listeners (e.g. this same hook elsewhere in
      // the tree) observe the write too.
      window.dispatchEvent(new StorageEvent('storage', { key, newValue }));
    },
    [key]
  );

  return [value, setValue];
}
